

import { useState, useEffect } from "react";
import {
  Form,
  useLoaderData,
  redirect,
  Link,
  useOutletContext,
} from "react-router-dom";
import {
  FormRow,
  FormRowSelect,
  SubmitBtn,
  AutoCompleteInput,
  ImageCarousel,
} from "../components";
import Wrapper from "../assets/wrappers/DashboardFormPage";
import { toast } from "react-toastify";
import { customFetch } from "../utils/custom-fetch.js";
import {
  RSS_NEWS,
  CITIES,
  WORK_ON_SATURDAY,
  MEMBER_TYPE,
  DID_YOU_KNOW,
} from "../../../utils/constants.js";
import ManagerFormModal from "../components/Building/ManagerFormModal";
import buildingTemplates from "../utils/buildingTemplates";

export const loader = async ({ params }) => {
  try {
    const { data } = await customFetch(`/buildings/${params.id}`);
    return data;
  } catch (error) {
    toast.error(error.response?.data?.msg);
    return redirect("/dashboard/all-buildings");
  }
};

export const action = async ({ request, params }) => {
  const formData = await request.formData();
  const data = Object.fromEntries(formData);

  if (data.maintenanceCompany === "undefined" || data.maintenanceCompany === "")
    delete data.maintenanceCompany;

  try {
    await customFetch.patch(`/buildings/${params.id}`, data);
    toast.success("הבניין עודכן בהצלחה");
    return redirect("/dashboard/all-buildings");
  } catch (error) {
    toast.error(error?.response?.data?.msg);
    return error;
  }
};

const EditBuilding = () => {
  const { building } = useLoaderData();
  const { user } = useOutletContext();
  const [showModal, setShowModal] = useState(false);

  const setMaintenanceCompanyId = (companyId) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      maintenanceCompany: companyId, // עדכון שדה חברת האחזקה
    }));
  };

  const [formData, setFormData] = useState({
    city: building.city || "",
    street: building.street || "",
    houseNumber: building.houseNumber || "",
    manager: building.manager || "",
    maintenanceCompany: building.maintenanceCompany || "",
    rssWeatherPath: building.rssWeatherPath || "",
    rssTitleNewsPath: building.rssTitleNewsPath || "",
    rssNewsPath: building.rssNewsPath || "",
    workOnSaturday: building.workOnSaturday || WORK_ON_SATURDAY.NO,
    memberType: building.memberType || MEMBER_TYPE.FREE,
    buildingType: building.buildingType || "מגורים",
    didYouKnow: building.didYouKnow || "יהדות ומסורת",
    designTemplate: building.designTemplate || "default",
  });

  const [selectedTemplate, setSelectedTemplate] = useState(
    formData.designTemplate
  );
  const [buildingType, setBuildingType] = useState(formData.buildingType);

  useEffect(() => {
    setFormData(building);
  }, [building]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveManager = () => {
    setShowModal(false);
  };

  const handleCityChange = (value) => {
    setFormData((prev) => ({ ...prev, city: value }));
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setFormData((prev) => ({ ...prev, designTemplate: template }));
  };

  const handleSelectBuildingType = (type) => {
    setBuildingType(type);
    setFormData((prev) => ({ ...prev, buildingType: type }));
  };

  return (
    <Wrapper>
      <Form method="post" className="form">
        <h4 className="form-title">עריכת בניין</h4>

        <div className="building-type-selection">
          <div className="building-type-cards">
            <div
              className={`building-type-card ${
                buildingType === "מגורים" ? "selected" : ""
              }`}
              onClick={() => handleSelectBuildingType("מגורים")}
            >
              <h6>בניין מגורים</h6>
            </div>
            <div
              className={`building-type-card ${
                buildingType === "משרדים" ? "selected" : ""
              }`}
              onClick={() => handleSelectBuildingType("משרדים")}
            >
              <h6>בניין משרדים</h6>
            </div>

            <div
              className={`building-type-card ${
                buildingType === "פרסום" ? "selected" : ""
              }`}
              onClick={() => handleSelectBuildingType("פרסום")}
            >
              <h6>פרסום</h6>
            </div>
          </div>
        </div>
        <input
          type="hidden"
          name="buildingType"
          value={formData.buildingType}
        />

        <div className="form-center">
          <AutoCompleteInput
            options={CITIES}
            name="city"
            placeholder="הכנס עיר"
            labelText="עיר"
            value={formData.city}
            onChange={(e) => handleCityChange(e.target.value)}
          />
          <input type="hidden" name="city" value={formData.city} />

          <FormRow
            type="text"
            name="street"
            labelText="רחוב"
            value={formData.street}
            defaultValue={formData.street}
            handleChange={handleChange}
            isRequired
          />

          <FormRow
            type="number"
            name="houseNumber"
            labelText="מספר בית"
            value={formData.houseNumber}
            defaultValue={formData.houseNumber}
            handleChange={handleChange}
            isRequired={true}
          />
          <FormRow
            type="text"
            name="nickname"
            labelText="כינוי הבניין"
            value={formData.nickname}
            defaultValue={formData.nickname}
            handleChange={handleChange}
          />

          {buildingType === "משרדים" && (
            <FormRowSelect
              labelText="מספר קומות בבניין"
              name="numberOfFloors"
              list={[...Array(50).keys()].map((i) => i + 1)}
              value={formData.numberOfFloors}
              handleChange={handleChange}
            />
          )}

          <FormRowSelect
            labelText="הידעת בחירת נושא"
            name="didYouKnow"
            list={Object.values(DID_YOU_KNOW)}
            value={formData.didYouKnow}
            handleChange={handleChange}
          />

          <FormRowSelect
            labelText="תחומי החדשות שיופיעו בכתוביות"
            name="rssTitleNewsPath"
            list={Object.values(RSS_NEWS)}
            value={formData.rssTitleNewsPath}
            handleChange={handleChange}
          />
          <FormRowSelect
            labelText="פעילות בשבת"
            name="workOnSaturday"
            list={Object.values(WORK_ON_SATURDAY)}
            value={formData.workOnSaturday}
            handleChange={handleChange}
          />

          {formData.maintenanceCompany && formData.maintenanceCompany._id && (
            <h4>
              חברת אחזקה : {formData.maintenanceCompany.name}{" "}
              {formData.maintenanceCompany.lastName}
            </h4>
          )}
          {formData.maintenanceCompany && (
            <button
              type="button"
              className="btn"
              onClick={async () => {
                try {
                  await customFetch.patch(
                    `/buildings/${building._id}/disconnect-maintenance`
                  );
                  toast.success("התנתקת מחברת האחזקה בהצלחה");
                  const { data } = await customFetch.get(
                    `/buildings/${building._id}`
                  );
                  setFormData(data.building);
                } catch (error) {
                  toast.error("שגיאה בניתוק חברת האחזקה");
                }
              }}
            >
              התנתקות מחברת האחזקה {formData.maintenanceCompany.name}{" "}
              {formData.maintenanceCompany.lastName}
            </button>
          )}

          {!formData.maintenanceCompany && (
            <button
              type="button"
              className="btn btn-block"
              onClick={() => setShowModal(true)}
            >
              קישור לחברת אחזקה
            </button>
          )}
          <input type="hidden" name="city" value={formData.city} />
        </div>
        <ImageCarousel
          selectedTemplate={selectedTemplate}
          onTemplateSelect={handleTemplateSelect}
        />

        <input type="hidden" name="designTemplate" value={selectedTemplate} />

        <input
          type="hidden"
          name="maintenanceCompany"
          value={
            // אם formData.maintenanceCompany הוא אובייקט, נחלץ ממנו את ה־_id
            typeof formData.maintenanceCompany === "object" &&
            formData.maintenanceCompany !== null
              ? formData.maintenanceCompany._id
              : formData.maintenanceCompany || ""
          }
        />

        <div className="btn-container">
          <SubmitBtn formBtn buttonText="שמירה" />
          <Link to="/dashboard/all-buildings" className="btn clear-btn">
            ביטול
          </Link>
        </div>
      </Form>

      {showModal && (user.role === "user" || user.role === "admin") && (
        <ManagerFormModal
          setMaintenanceCompanyId={setMaintenanceCompanyId}
          closeModal={() => setShowModal(false)}
          handleSave={handleSaveManager}
        />
      )}

      <style>
        {`
  .building-type-cards {
    display: flex;
    justify-content: space-between;
    margin-top: 1rem;
  }

  .building-type-card {
    width: 48%;
    padding: 20px;
    border: 1px solid var(--grey-500);
    border-radius: var(--borderRadius);
    cursor: pointer;
    text-align: center;
    transition: all 0.3s ease;
  }

  .building-type-card.selected {
    background-color: var(--primary-500);
    color: white;
  }

  .template-gallery {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    margin-top: 1rem;
    margin-bottom: 1rem;
  }

  .template-card {
    flex-basis: 30%;
    
    margin: 10px;
    opacity: 0.7;
    border-radius: var(--borderRadius);
    cursor: pointer;
    text-align: center;
    justify-content: space-between;
    transition: all 0.3s ease;
    border: 1px solid var(--grey-500);
  }

  .template-card.selected {
    border: 3px solid var(--primary-500);
    opacity: 1;
  }

  .template-card img {
    width: 100%;
    height: 150px;
    object-fit: cover;
    margin-bottom: 1.5rem;
  }

  .plan-cards {
    display: flex;
    justify-content: space-between;
    margin-top: 1rem;
    margin-bottom: 1rem;
  }

  .plan-card {
    width: 48%;
    padding: 20px;
    border: 1px solid var(--grey-500);
    border-radius: var(--borderRadius);
    cursor: pointer;
    text-align: center;
    transition: all 0.3s ease;
  }

  .plan-card.selected {
    background-color: var(--primary-500);
    color: white;
  }

  .plan-card h6 {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }

  .plan-card p {
    font-size: 1rem;
  }

  /* רספונסיביות */
  @media (max-width: 768px) {
    .template-card {
      flex-basis: 100%;
      margin-top: 1rem;
    }

    .plan-card {
      margin-bottom: 1rem;
    }
  }

  @media (min-width: 768px) and (max-width: 1024px) {
    .template-card {
      flex-basis: 48%;
      margin: 20px;
    }
  }

  .modal {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 1000;
    }

    .modal-content {
      background: white;
      padding: 20px;
      border-radius: 5px;
      max-width: 500px;
      width: 100%;
    }

    .modal-actions {
      display: flex;
      justify-content: space-between;
      margin-top: 20px;
    }
`}
      </style>
    </Wrapper>
  );
};

export default EditBuilding;
